/**
 * The two shapes these operations screens need that a table cannot draw: a
 * count over time, and a ranked breakdown of a whole.
 *
 * Both are plain SVG with an HTML overlay for the hit areas: `recharts` is in the
 * monorepo but a stacked column chart whose categories are a closed set of
 * outcome classes, with a keyboard-reachable readout and a tabular fallback,
 * is less code drawn directly than configured. Colours are the console's own
 * tokens referenced as CSS variables, so nothing new enters the palette.
 *
 * The rule both obey: an empty bucket is drawn as an empty slot at its true
 * position rather than closed up. A gap in reporting and a quiet hour are
 * different facts and must not be merged into one smooth line.
 */

import { useRef, useState, type ReactNode } from 'react';
import { cx } from '@ui/utils/cx';
import { Bar } from '@ui/console/measures';
import { formatCount, formatPercent, Quantity } from '@ui/console/measures';
import type { Tone } from '@ui/console/status';

export const TONE_VARIABLE: Record<Tone, string> = {
  success: 'var(--color-bg-success-solid)',
  warning: 'var(--color-bg-warning-solid)',
  error: 'var(--color-bg-error-solid)',
  info: 'var(--color-bg-brand-solid)',
  neutral: 'var(--color-fg-quaternary)',
};

export type SeriesKey = { id: string; label: string; tone: Tone };

export type TimeBucket = {
  /** Start of the bucket, as epoch milliseconds. */
  start: number;
  /** How the bucket reads on an axis, e.g. `09:00` or `18 Sep`. */
  label: string;
  /** Count per series id. A series absent from the map counts zero. */
  counts: Record<string, number>;
};

/**
 * Slice timestamps into evenly spaced buckets across the whole span, so a
 * period with nothing reported still gets a column of its own.
 */
export function bucketize<T>(
  items: readonly T[],
  at: (item: T) => number,
  seriesOf: (item: T) => string,
  buckets: number,
  span?: { since: number; until: number },
): TimeBucket[] {
  const times = items.map(at).filter((value) => Number.isFinite(value));
  if (!span && !times.length) return [];
  const since = span ? span.since : Math.min(...times);
  const until = span ? span.until : Math.max(...times);
  const count = Math.max(1, buckets);
  // A single instant would give a zero-width span and put every item in the
  // last bucket; one minute keeps the columns evenly spaced instead.
  const width = Math.max(1, (until - since) / count) || 60_000;
  const slots: TimeBucket[] = Array.from({ length: count }, (_, index) => {
    const start = since + index * width;
    return { start, label: axisLabel(start, width), counts: {} };
  });
  for (const item of items) {
    const time = at(item);
    if (!Number.isFinite(time)) continue;
    const index = Math.min(count - 1, Math.max(0, Math.floor((time - since) / width)));
    const slot = slots[index];
    if (!slot) continue;
    const key = seriesOf(item);
    slot.counts[key] = (slot.counts[key] ?? 0) + 1;
  }
  return slots;
}

/** Hours when the buckets are within a day, otherwise a date. */
function axisLabel(start: number, width: number): string {
  const date = new Date(start);
  if (width < 86_400_000) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
}

function bucketTotal(bucket: TimeBucket): number {
  return Object.values(bucket.counts).reduce((sum, value) => sum + value, 0);
}

function bucketSentence(bucket: TimeBucket, series: readonly SeriesKey[], noun: string): string {
  const total = bucketTotal(bucket);
  const when = new Date(bucket.start).toLocaleString();
  if (!total) return `Nothing was reported in the period beginning ${when}.`;
  const parts = series
    .filter((key) => (bucket.counts[key.id] ?? 0) > 0)
    .map((key) => `${formatCount(bucket.counts[key.id] ?? 0)} ${key.label}`);
  return `${when}: ${formatCount(total)} ${noun} — ${parts.join(', ')}.`;
}

/**
 * A count over time, stacked by outcome class.
 *
 * The y axis is shared across every column so two periods are comparable by
 * height, which is the entire question this chart is asked: did it get worse.
 */
export function Timeline({
  buckets,
  series,
  noun,
  caption,
}: {
  buckets: readonly TimeBucket[];
  series: readonly SeriesKey[];
  /** What one item is, used in the readout: `events`, `messages`. */
  noun: string;
  caption?: ReactNode;
}) {
  const [readout, setReadout] = useState<number | undefined>(undefined);
  const [active, setActive] = useState(0);
  const hits = useRef(new Map<number, HTMLButtonElement>());
  const peak = Math.max(1, ...buckets.map(bucketTotal));
  const total = buckets.reduce((sum, bucket) => sum + bucketTotal(bucket), 0);
  const width = buckets.length ? 100 / buckets.length : 100;

  const move = (next: number) => {
    const clamped = Math.min(Math.max(next, 0), buckets.length - 1);
    setActive(clamped);
    hits.current.get(clamped)?.focus();
  };

  const shown = readout === undefined ? undefined : buckets[readout];
  const sentence = buckets
    .map((bucket) => `${bucket.label}: ${formatCount(bucketTotal(bucket))}`)
    .join(', ');

  return (
    <figure className="m-0 flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
      {caption ? <figcaption className="text-sm text-tertiary">{caption}</figcaption> : null}

      <div className="relative h-40 w-full border-b border-secondary">
        <svg
          className="h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {buckets.map((bucket, index) => {
            let offset = 0;
            const columnHeight = (bucketTotal(bucket) / peak) * 100;
            return (
              <g key={`col-${bucket.start}-${index}`}>
                <rect
                  x={index * width}
                  width={Math.max(width - 0.4, 0.2)}
                  y={0}
                  height={100}
                  fill={
                    readout === index
                      ? 'var(--color-bg-secondary_hover, var(--color-bg-secondary))'
                      : 'var(--color-bg-secondary)'
                  }
                />
                {series.map((key) => {
                  const count = bucket.counts[key.id] ?? 0;
                  if (!count) return null;
                  const height = (count / peak) * 100;
                  offset += height;
                  return (
                    <rect
                      key={key.id}
                      x={index * width}
                      width={Math.max(width - 0.4, 0.2)}
                      y={100 - offset}
                      height={Math.max(height, columnHeight > 0 ? 0.8 : 0)}
                      fill={TONE_VARIABLE[key.tone]}
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>

        <div
          role="group"
          aria-label={`Counts over time. Use the arrow keys to read each period.`}
          className="absolute inset-0"
          onMouseLeave={() => setReadout(undefined)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              move(active + 1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              move(active - 1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              move(0);
            } else if (event.key === 'End') {
              event.preventDefault();
              move(buckets.length - 1);
            }
          }}
        >
          {buckets.map((bucket, index) => (
            <button
              key={`hit-${bucket.start}-${index}`}
              type="button"
              data-bucket-index={index}
              data-bucket-total={bucketTotal(bucket)}
              tabIndex={index === active ? 0 : -1}
              ref={(element) => {
                if (element) hits.current.set(index, element);
                else hits.current.delete(index);
              }}
              aria-label={bucketSentence(bucket, series, noun)}
              className="absolute top-0 bottom-0 outline-brand focus-visible:outline-2"
              style={{ left: `${index * width}%`, width: `${Math.max(width, 0.2)}%` }}
              onMouseEnter={() => setReadout(index)}
              onFocus={() => {
                setActive(index);
                setReadout(index);
              }}
              onBlur={() => setReadout(undefined)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-between text-xs text-quaternary tabular-nums">
        <span>{buckets[0]?.label ?? '—'}</span>
        <span>{buckets.at(-1)?.label ?? '—'}</span>
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {series.map((key) => (
          <li key={key.id} className="flex items-center gap-1.5 text-xs text-tertiary">
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: TONE_VARIABLE[key.tone] }}
              aria-hidden="true"
            />
            {key.label}
          </li>
        ))}
      </ul>

      <div
        className="min-h-12 rounded-lg border border-secondary bg-secondary px-3 py-2"
        aria-live="polite"
      >
        {shown ? (
          <p className="text-sm text-secondary select-text">
            {bucketSentence(shown, series, noun)}
          </p>
        ) : (
          <p className="text-sm text-tertiary">
            Point at a column, or tab into the chart and use the arrow keys, to read its exact
            counts. {formatCount(total)} {noun} across {formatCount(buckets.length)} periods.
          </p>
        )}
      </div>

      <p className="sr-only">{sentence}</p>
    </figure>
  );
}

export type RankedRow = {
  id: string;
  label: ReactNode;
  count: number;
  tone?: Tone;
  /** Shown to the right of the share, for a second figure such as a cost. */
  trailing?: ReactNode;
};

/**
 * A ranked breakdown where every row is drawn against the same total, so the
 * longest bar is the answer and the numbers are only read afterwards.
 */
export function Ranked({
  rows,
  total,
  unit,
  emptyHint,
  limit = 12,
}: {
  rows: readonly RankedRow[];
  /** The denominator every share is taken against. */
  total: number;
  unit: string;
  emptyHint: string;
  limit?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const ordered = [...rows].sort((left, right) => right.count - left.count);
  const visible = expanded ? ordered : ordered.slice(0, limit);
  if (!ordered.length) {
    return <p className="text-sm text-tertiary">{emptyHint}</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2.5">
        {visible.map((row) => {
          const fraction = total > 0 ? row.count / total : 0;
          return (
            <li key={row.id} className="flex min-w-0 flex-col gap-1">
              <span className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-secondary">{row.label}</span>
                <span className="flex shrink-0 items-baseline gap-2 tabular-nums">
                  <Quantity value={formatCount(row.count)} unit={unit} raw={row.count} />
                  <span className="w-12 text-right text-xs text-tertiary">
                    {total > 0 ? formatPercent(fraction) : '—'}
                  </span>
                  {row.trailing ? (
                    <span className="text-xs text-tertiary">{row.trailing}</span>
                  ) : null}
                </span>
              </span>
              <Bar
                fraction={fraction}
                tone={row.tone ?? 'info'}
                label={`${row.id}: ${formatCount(row.count)} of ${formatCount(total)} ${unit}`}
              />
            </li>
          );
        })}
      </ul>
      {ordered.length > limit ? (
        <button
          type="button"
          className={cx(
            'self-start rounded-md text-xs font-medium text-brand-secondary underline',
            'outline-brand focus-visible:outline-2',
          )}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded
            ? `Show the top ${limit} only`
            : `Show all ${formatCount(ordered.length)} rows (${formatCount(ordered.length - limit)} more)`}
        </button>
      ) : null}
    </div>
  );
}
